/**
 * RudderStack Web Analytics Initialization
 *
 * ENVIRONMENT RULES
 * ----------------------------------------------------------------------------
 *
 * ============================================================================
 * 1) STORAGE BEHAVIOR (anonymousId persistence + identity isolation)
 * ============================================================================
 *
 * ✔ Production (ISOLATED FROM Other Production Environments eg patient portal / customer flow)
 *    - Uses localStorage as primary storage
 *    - Enables `sameDomainCookiesOnly: true` so fallback cookies *never* leak into
 *      from prod's top-level-domain cookies
 *    - Fallback cookie name is still the default `rl_anonymous_id`
 *      but it is restricted to the *production* domain only
 *    - Reads/writes: localStorage (cookie only if storage is blocked)
 *
 *
 * ✔ Staging (STRICTLY ISOLATED FROM PRODUCTION)
 *    - Primary: sessionStorage
 *    - `sameDomainCookiesOnly: true`
 *         → ensures fallback cookies CANNOT read/write prod apex cookies
 *         → staging cookie stays scoped to staging subdomain only
 *    - Fallback cookie name: `rl_anonymous_id`
 *         (but isolated to staging domain due to sameDomainCookiesOnly)
 *
 *
 * ✔ Development / Local / Preview
 *    - Primary: sessionStorage
 *    - `sameDomainCookiesOnly: true`
 *         → prevents accidental collisions with staging/prod cookies
 *    - Fallback cookie name: `rl_anonymous_id` (isolated to dev host)
 *
 *
 * ✔ Why no custom cookie names?
 *    - Rudderstack doesnt allow for read/write custom cookie. (only read)
 *    - Preferred Isolation would be Staging / Prod in different apex domain
 *    - RudderStack already mostly isolates environs via sameDomainCookiesOnly
 *    - Note: Prod can bleed cookie to staging as part of its fallback, however
 *    - this potential identity pollution is limited to staging and not prod.
 *
 *
 * ============================================================================
 * 2) SESSION CONTROL (how sessions start/end + expiration behavior)
 * ============================================================================
 *
 * ✔ Production/Staging/Development - TBD (to be determined)
 *    - autoTrack: enabled
 *    - Session persists across tabs/windows via cookie/localStorage
 *    - Standard inactivity timeout: ~30 minutes
 *
 *
 * ============================================================================
 * 3) DESTINATION CONTROL (where events are allowed to go)
 * ============================================================================
 *    - `destinations: []`
 *         → Blocks all client-side and cloud destinations
 *         → Still forwards events to RudderStack backend
 *         → Warehouse loaders (e.g., BigQuery) still ingest events
 *    - Ensures staging traffic cannot leak into marketing/analytics tools
 *
 * ✔ Production/Staging/Development
 *    - Recommended: `destinations: []`
 *
 *
 * ============================================================================
 * 4) WORKSPACE / ROUTING CONTROL
 * ============================================================================
 *
 * ✔ Each environment uses separate:
 *       • workspace
 *       • writeKey
 *       • dataplane URL
 *
 *   This guarantees hard separation of routing, identity, governance,
 *   and warehouse syncs across prod / staging / dev.
 */

/* oxlint-disable no-console */
import { type ApiObject, type IdentifyTraits } from '@rudderstack/analytics-js';
import { RudderAnalytics } from '@rudderstack/analytics-js';

import { isProd, isStaging, isTest } from './env.utils';
import { isBrowserWebdriver } from './is-browser-webdriver';
import { shouldSuppressSupportAnalytics } from './support-concierge-analytics-privacy';

const ENV_CONFIG = {
  production: {
    writeKey: '2zyFmi4YArhb18gMhCFjEV2dK2N',
    dataplane: 'https://berrystreesjqs.dataplane.rudderstack.com',
    workspaceId: '2fb5v5RnhD7GNbP71rEmrCEfNVt',

    // prod = sessionStorage + isolation
    sameDomainCookiesOnly: true,
    storage: {
      type: 'localStorage' as const,
      cookie: {
        secure: true,
        samesite: 'Lax',
      },
    },
    // Use Beacon API for non-blocking, async event delivery
    // This prevents ad blockers from causing timeouts that block UI
    useBeacon: true,
    beaconQueueOptions: {
      maxItems: 10,
      flushQueueInterval: 60000, // 60 seconds
    },
  },

  staging: {
    writeKey: '36CWsiRvwCMc2idluFfJ9IqNtFQ',
    dataplane: 'https://berrystreehhne.dataplane.rudderstack.com',
    workspaceId: '2l114Eym6RZYUkRnteL0ppKGQ4U',

    // staging = sessionStorage + isolation
    sameDomainCookiesOnly: true,
    storage: {
      type: 'sessionStorage' as const,
      cookie: {
        secure: true,
        samesite: 'Lax',
      },
    },
    useBeacon: true,
    beaconQueueOptions: {
      maxItems: 10,
      flushQueueInterval: 60000,
    },
  },

  development: {
    writeKey: '36CVz5urTZPAr5yLG1tmfYISsfP',
    dataplane: 'https://berrystreexpbu.dataplane.rudderstack.com',
    workspaceId: '30eAkfCkODfFNKHDEmVyhUGOymd',

    // dev = isolated sessionStorage
    sameDomainCookiesOnly: true,
    storage: {
      type: 'sessionStorage' as const,
      cookie: {
        secure: true,
        samesite: 'Lax',
      },
    },
    useBeacon: true,
    beaconQueueOptions: {
      maxItems: 10,
      flushQueueInterval: 60000,
    },
  },
};

/**
 * Select current environment config
 */
const CURRENT = isProd
  ? ENV_CONFIG.production
  : isStaging
    ? ENV_CONFIG.staging
    : ENV_CONFIG.development;

/**
 * ---------------------------------------------------------------------------
 * INITIALIZER
 * ---------------------------------------------------------------------------
 */
const init = () => {
  if (typeof window === 'undefined') return undefined;
  if (isTest || isBrowserWebdriver()) return undefined;

  try {
    const analytics = new RudderAnalytics();

    analytics.load(CURRENT.writeKey, CURRENT.dataplane, {
      polyfillIfRequired: false,
      storage: CURRENT.storage,
      sameDomainCookiesOnly: CURRENT.sameDomainCookiesOnly,
      useBeacon: CURRENT.useBeacon,
      beaconQueueOptions: CURRENT.beaconQueueOptions,

      getSourceConfig: () => ({
        updatedAt: new Date().toISOString(),
        source: {
          workspaceId: CURRENT.workspaceId,
          updatedAt: new Date().toISOString(),
          enabled: !isTest,
          destinations: [],

          // RudderStack uses writeKey as source ID for synthetic config
          id: CURRENT.writeKey,
          writeKey: CURRENT.writeKey,

          config: {
            statsCollection: {
              errors: { enabled: false },
              metrics: { enabled: false },
            },
          },
        },
      }),
    });

    return analytics;
  } catch (err) {
    console.error('Error initializing RudderStack:', err);
    return undefined;
  }
};

const sdk = init();

// Export singleton instance with simple error handling
export const rudderstackAnalytics: {
  track: (event: string, properties?: ApiObject) => void;
  identify: (userId: string, traits?: IdentifyTraits) => void;
  page: (name: string, properties?: ApiObject) => void;
  setAnonymousId: (id: string) => void;
  getAnonymousId: () => string | undefined;
  reset: () => void;
  initialized: boolean;
} = {
  track(event, properties) {
    if (shouldSuppressSupportAnalytics(properties)) return;
    if (sdk) sdk.track(event, properties);
    else console.error('SDK not initialized for track call');
  },

  identify(userId, traits) {
    if (sdk) sdk.identify(userId, traits);
    else console.error('SDK not initialized for identify call');
  },

  page(name, properties) {
    if (shouldSuppressSupportAnalytics(properties)) return;
    if (sdk) return sdk.page(name, properties);
    else console.error('SDK not initialized for page call');
  },

  setAnonymousId(id) {
    if (sdk) return sdk.setAnonymousId(id);
    else console.error('SDK not initialized for setAnonymousId call');
  },

  getAnonymousId() {
    if (sdk) return sdk.getAnonymousId();
    console.error('SDK not initialized for getAnonymousId call');
    return undefined;
  },

  reset() {
    if (sdk) return sdk.reset(true);
    else console.error('SDK not initialized for reset call');
  },

  initialized: sdk !== undefined,
};
